/**
 * WorkLifecycleContext — UNIFIED_WORK_LIFECYCLE_CONTEXT_PERSISTENCE_V1
 *
 * Persists lifecycle context (requestId, gwuId, receiptId, relationshipId) across
 * refresh, route changes, and return visits via localStorage.
 *
 * BOUNDARY:
 * - persisted context = navigation memory
 * - persisted context ≠ approval
 * - persisted context ≠ execution authority
 * - clearing context has no operational effect
 *
 * INVARIANTS:
 * - Never mutates governed state
 * - Never triggers execution
 * - fail-safe: any parse error resets to empty context
 * - Context is advisory only — consumers must not grant authority from it
 */

const STORAGE_KEY = 'trustgate:work-lifecycle-context:v1';
const CONTEXT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export interface WorkLifecycleContext {
  requestId?: string;
  gwuId?: string;
  receiptId?: string;
  relationshipId?: string;
  /** ISO timestamp when context was last written */
  savedAt?: string;
  /** Human-readable label for the active work item (e.g. request title or GWU hint) */
  label?: string;
}

const EMPTY_CONTEXT: WorkLifecycleContext = {};

function isContextStale(ctx: WorkLifecycleContext): boolean {
  if (!ctx.savedAt) return false;
  try {
    const age = Date.now() - new Date(ctx.savedAt).getTime();
    return age > CONTEXT_TTL_MS;
  } catch {
    return true;
  }
}

function hasAnyId(ctx: WorkLifecycleContext): boolean {
  return !!(ctx.requestId || ctx.gwuId || ctx.receiptId || ctx.relationshipId);
}

/**
 * Load persisted context from localStorage.
 * Returns EMPTY_CONTEXT on any parse error.
 */
export function loadLifecycleContext(): WorkLifecycleContext {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_CONTEXT;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return EMPTY_CONTEXT;

    // Validate shape
    const ctx: WorkLifecycleContext = {};
    if (typeof parsed.requestId === 'string') ctx.requestId = parsed.requestId;
    if (typeof parsed.gwuId === 'string') ctx.gwuId = parsed.gwuId;
    if (typeof parsed.receiptId === 'string') ctx.receiptId = parsed.receiptId;
    if (typeof parsed.relationshipId === 'string') ctx.relationshipId = parsed.relationshipId;
    if (typeof parsed.savedAt === 'string') ctx.savedAt = parsed.savedAt;
    if (typeof parsed.label === 'string') ctx.label = parsed.label;

    return ctx;
  } catch {
    return EMPTY_CONTEXT;
  }
}

/**
 * Persist lifecycle context to localStorage.
 * Merges with existing context — only provided fields are updated.
 */
export function saveLifecycleContext(updates: Partial<WorkLifecycleContext>): WorkLifecycleContext {
  const current = loadLifecycleContext();
  const next: WorkLifecycleContext = {
    ...current,
    ...updates,
    savedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage unavailable — silently no-op
  }
  return next;
}

/**
 * Clear all lifecycle context from localStorage.
 */
export function clearLifecycleContext(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable — silently no-op
  }
}

/**
 * Detect context issues for UI warnings.
 */
export interface ContextHealthCheck {
  hasContext: boolean;
  isStale: boolean;
  missingIds: string[];
}

export function checkContextHealth(ctx: WorkLifecycleContext): ContextHealthCheck {
  const missingIds: string[] = [];
  if (!ctx.requestId) missingIds.push('requestId');
  if (!ctx.gwuId) missingIds.push('gwuId');

  return {
    hasContext: hasAnyId(ctx),
    isStale: isContextStale(ctx),
    missingIds,
  };
}
